import type Alpine from 'alpinejs';

import {CleanupManager} from './cleanup-manager';

type AlpineDataFactory<T, TArgs extends unknown[] = []> = (
    ...args: TArgs
) => Alpine.AlpineComponent<T>;
type AlpineDataRegistrar = (AlpineInstance: Alpine.Alpine) => void;

const cleanupOwner = Symbol('trellis-ui.cleanup-owner');
type CleanupOwner = () => CleanupManager;

const findParentCleanup = (
    AlpineInstance: Alpine.Alpine,
    el: Alpine.ElementWithXAttributes,
): CleanupManager | undefined => {
    const parent = el.parentElement as Alpine.ElementWithXAttributes | null;
    if (!parent) return undefined;
    return AlpineInstance.closestDataStack(parent)
        .map((data) => (Reflect.get(data, cleanupOwner) as CleanupOwner | undefined)?.())
        .find((cleanup) => cleanup !== undefined);
};

const defineAlpineData = <T, TArgs extends unknown[] = []>(
    name: string,
    factory: AlpineDataFactory<T, TArgs>,
): AlpineDataRegistrar => (
    AlpineInstance,
) => {
    AlpineInstance.data(name, function(this: unknown, ...args: TArgs) {
        const component = factory.apply(this, args);
        const cleanup = new CleanupManager();
        const init = component.init;
        const destroy = component.destroy;

        Reflect.defineProperty(component, cleanupOwner, {value: () => cleanup, configurable: true});
        component.init = function(): void {
            const el = Reflect.get(this, '$el') as Alpine.ElementWithXAttributes;
            findParentCleanup(AlpineInstance, el)?.addChild(cleanup);
            cleanup.add(() => destroy?.call(this));
            init?.call(this);
        };
        component.destroy = function(): void {
            cleanup.run();
        };
        return component;
    });
};

export {defineAlpineData};
export type {AlpineDataFactory, AlpineDataRegistrar};
